import React from 'react';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { Row } from '@tanstack/react-table';

import { getTableCellErrorState } from './hooks/get-table-cell-error-state';
import { TableCellDisplay } from './table-integer-display';
import { TableTextInput } from './table-text-input';

export interface TableTextWithDuplicateErrorProps {
  row: Row<DealItemWithVersions>;
  field: 'sku' | 'gtin';
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  disabled?: boolean;
  autoFocus?: boolean;
  displayValue: string | null;
  isEditing: boolean;
  /** Whether this field was recently modified (for highlighting) */
  isRecentlyModified?: boolean;
}

/**
 * Text input with duplicate error detection for SKU/GTIN
 * Handles error styling in both edit and display modes
 */
export const TableTextWithDuplicateError: React.FC<
  TableTextWithDuplicateErrorProps
> = ({
  row,
  field,
  value,
  onChange,
  onBlur,
  onKeyDown,
  disabled,
  autoFocus,
  displayValue,
  isEditing,
  isRecentlyModified = false,
}) => {
  const isOfferLocked = useIsOfferLocked();
  const isParent = row.subRows.length > 1;

  const errorState = getTableCellErrorState({
    row,
    field,
    errorType: 'duplicate',
  });

  return (
    <>
      {isEditing && !isParent ? (
        <TableTextInput
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          hasError={errorState.hasError}
        />
      ) : (
        <TableCellDisplay
          value={displayValue}
          isEmpty={errorState.isEmpty}
          hasError={errorState.hasError}
          errorMessage={errorState.errorMessage}
          isRecentlyModified={isRecentlyModified}
          isEditable={!isOfferLocked}
          isParent={isParent}
        />
      )}
    </>
  );
};
